export const cefrLevels = {
  A1: {
    level: 'A1',
    name: 'Beginner',
    description: 'Can understand and use familiar everyday expressions and very basic phrases.',
    color: 'bg-emerald-100 text-emerald-800',
    textColor: 'text-emerald-800',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-200',
    examples: [
      'What is your name?',
      'Where do you live?',
      'Can you count to ten?'
    ],
    characteristics: {
      vocabulary: 'Very basic, concrete vocabulary about personal details and everyday objects',
      grammar: 'Simple sentence structures, present tense only',
      complexity: 'Very simple questions and statements'
    }
  },
  A2: {
    level: 'A2',
    name: 'Elementary',
    description: 'Can communicate in simple and routine tasks on familiar topics.',
    color: 'bg-green-100 text-green-800',
    textColor: 'text-green-800',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    examples: [
      'What did you do yesterday?',
      'Can you describe your family?',
      'What will you do tomorrow?'
    ],
    characteristics: {
      vocabulary: 'Simple vocabulary related to routine situations and basic needs',
      grammar: 'Simple present, past and future forms, basic conjunctions',
      complexity: 'Simple descriptions and social exchanges'
    }
  },
  B1: {
    level: 'B1',
    name: 'Intermediate',
    description: 'Can deal with most situations likely to arise while traveling.',
    color: 'bg-blue-100 text-blue-800',
    textColor: 'text-blue-800',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    examples: [
      'Explain the difference between these two options.',
      'What would you do if you were in this situation?',
      'How does this system work?'
    ],
    characteristics: {
      vocabulary: 'Sufficient vocabulary to express opinions and explain basic viewpoints',
      grammar: 'Various tenses, conditionals, comparatives',
      complexity: 'Connected text on familiar topics, explanations of opinions'
    }
  },
  B2: {
    level: 'B2',
    name: 'Upper-Intermediate',
    description: 'Can interact with a degree of fluency and spontaneity with native speakers.',
    color: 'bg-indigo-100 text-indigo-800',
    textColor: 'text-indigo-800',
    bgColor: 'bg-indigo-100',
    borderColor: 'border-indigo-200',
    examples: [
      'Analyze the causes and effects of this phenomenon.',
      'Compare and contrast these different approaches.',
      'What evidence supports this theory?'
    ],
    characteristics: {
      vocabulary: 'Wide vocabulary for most general topics, some specialized terminology',
      grammar: 'Complex sentence structures, passive voice, reported speech',
      complexity: 'Clear detailed text on complex subjects, argumentative discourse'
    }
  },
  C1: {
    level: 'C1',
    name: 'Advanced',
    description: 'Can express ideas fluently and spontaneously without much searching for expressions.',
    color: 'bg-purple-100 text-purple-800',
    textColor: 'text-purple-800',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    examples: [
      'Critically evaluate the implications of this research.',
      'Synthesize information from multiple sources to form a cohesive argument.',
      'To what extent do you agree with this perspective?'
    ],
    characteristics: {
      vocabulary: 'Extensive vocabulary including idiomatic expressions and connotations',
      grammar: 'All structures used accurately and appropriately',
      complexity: 'Complex arguments, hypothetical scenarios, nuanced opinions'
    }
  },
  C2: {
    level: 'C2',
    name: 'Proficient',
    description: 'Can understand with ease virtually everything heard or read.',
    color: 'bg-pink-100 text-pink-800',
    textColor: 'text-pink-800',
    bgColor: 'bg-pink-100',
    borderColor: 'border-pink-200',
    examples: [
      'Deconstruct the author\'s rhetorical devices and their effect on the audience.',
      'Formulate an original theory that reconciles these conflicting perspectives.',
      'Elucidate the subtle nuances between these seemingly identical concepts.'
    ],
    characteristics: {
      vocabulary: 'Near native-like vocabulary, including specialized academic and professional terminology',
      grammar: 'Complete mastery of complex structures, subtle distinctions in meaning',
      complexity: 'Sophisticated academic discourse, nuanced argumentation, abstract concepts'
    }
  }
};

export const detectCefrLevel = (questionText) => {
  const text = questionText.toLowerCase();
  
  // Simple heuristics based on text complexity
  const wordCount = text.split(/\s+/).length;
  const avgWordLength = text.length / wordCount;
  const complexWordCount = text.split(/\s+/).filter(word => word.length > 8).length;
  const complexityRatio = complexWordCount / wordCount;
  
  // Academic/specialized vocabulary indicators
  const academicTerms = [
    'analyze', 'evaluate', 'synthesize', 'critique', 'examine', 'assess', 'investigate',
    'implications', 'perspective', 'theoretical', 'methodology', 'framework', 'paradigm'
  ];
  
  const academicTermCount = academicTerms.filter(term => text.includes(term)).length;
  
  // Sentence complexity indicators
  const sentenceCount = text.split(/[.!?]+/).length - 1;
  const avgWordsPerSentence = sentenceCount > 0 ? wordCount / sentenceCount : wordCount;
  
  // Assign CEFR level based on combined factors
  if (avgWordLength > 6 && complexityRatio > 0.2 && academicTermCount > 3 && avgWordsPerSentence > 20) {
    return 'C2'; // Proficient
  } else if (avgWordLength > 5.5 && complexityRatio > 0.15 && academicTermCount > 2 && avgWordsPerSentence > 18) {
    return 'C1'; // Advanced
  } else if (avgWordLength > 5 && complexityRatio > 0.1 && academicTermCount > 1 && avgWordsPerSentence > 15) {
    return 'B2'; // Upper-Intermediate
  } else if (avgWordLength > 4.5 && complexityRatio > 0.05 && avgWordsPerSentence > 12) {
    return 'B1'; // Intermediate
  } else if (avgWordLength > 4 && avgWordsPerSentence > 8) {
    return 'A2'; // Elementary
  } else {
    return 'A1'; // Beginner
  }
};

export const getCefrLevel = (levelKey) => {
  return cefrLevels[levelKey] || null;
};

export const getAllCefrLevels = () => {
  return Object.entries(cefrLevels).map(([key, value]) => ({ key, ...value }));
};